import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { getProduct, mediaUrl } from "../lib/catalogueApi";
import { addToCart, serverMsg } from "../lib/cartApi";
import { StoreLayout } from "./StoreLayout";
import { Breadcrumbs } from "./Breadcrumbs";
import { tileProps } from "./NoPhoto";
import { Gallery } from "./product/Gallery";
import { BuyBox } from "./product/BuyBox";
import { StickyBuyBar } from "./product/StickyBuyBar";
import { specGroups, keyHighlights } from "./product/specs";
import { ClearancePdpBlock } from "./ClearanceBits";

const Details = lazy(() => import("./product/Details"));

const Skeleton = () => (
  <StoreLayout>
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="product-skeleton">
      <div className="mb-6 h-4 w-72 rounded bg-slate-100" />
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className="aspect-square animate-pulse rounded-2xl border border-slate-200 bg-white" />
        <div className="space-y-4"><div className="h-8 rounded bg-slate-100" /><div className="h-8 w-2/3 rounded bg-slate-100" /><div className="h-40 rounded-2xl bg-slate-100" /></div>
      </div>
    </div>
  </StoreLayout>
);

function useJsonLd(p, images) {
  useEffect(() => {
    if (!p) return;
    document.title = `${p.display_name} - Rhyte Step IT`;
    const abs = (u) => (u.startsWith("http") ? u : `${window.location.origin}${u.replace(window.location.origin, "")}`);
    const ld = {
      "@context": "https://schema.org", "@type": "Product", name: p.display_name, sku: p.sku || undefined, mpn: p.mpn || undefined,
      gtin: p.gtin || undefined, description: p.short_description || undefined,
      brand: p.brand ? { "@type": "Brand", name: p.brand.display_name } : undefined,
      image: images.length ? images.slice(0, 5).map(abs) : undefined,
      offers: { "@type": "Offer", priceCurrency: "AUD", price: Number(p.price_inc_gst) > 0 ? p.price_inc_gst : undefined, url: window.location.href,
        availability: p.is_sellable ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
    };
    document.querySelectorAll('script[type="application/ld+json"]:not(#ld-product)').forEach((s) => { if (s.textContent.includes('"Product"')) s.remove(); });
    let s = document.getElementById("ld-product");
    if (!s) { s = document.createElement("script"); s.id = "ld-product"; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
  }, [p, images]);
}

export default function ProductPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [addEl, setAddEl] = useState(null);

  useEffect(() => {
    setP(null); setErr(false); setQty(1);
    getProduct(slug).then(setP).catch(() => setErr(true));
  }, [slug]);

  const images = useMemo(() => {
    if (!p) return [];
    const ic = p.icecat_content;
    const icImgs = ic && ic.image_source === "ICECAT" && (ic.gallery || []).length ? ic.gallery.map((g) => g.url).filter(Boolean) : null;
    return icImgs || p.media.map((m) => mediaUrl(m.id));
  }, [p]);
  useJsonLd(p, images);

  const add = async () => {
    setAdding(true);
    try { await addToCart(p.id, qty); }  // T52: the mini cart drawer confirms
    catch (e) { toast.error(serverMsg(e, "Couldn't add this item to your cart.")); }
    finally { setAdding(false); }
  };

  if (err) return <StoreLayout><div className="mx-auto max-w-6xl px-5 py-20 text-center text-slate-500" data-testid="product-notfound">Product not found.</div></StoreLayout>;
  if (!p) return <Skeleton />;

  const ic = p.icecat_content;
  const html = (ic?.long_description_html || p.long_description_html || "").trim() || (p.short_description ? `<p>${p.short_description}</p>` : "");
  const groups = specGroups(p);
  const highlights = keyHighlights(p, groups);
  const docs = [...(ic?.documents || []).map((d) => ({ url: d.rhyte_storage_url, title: d.title || d.document_type })), ...(p.documents || [])].filter((d) => d.url);
  const sections = [
    (html || ic?.reasons_to_buy?.length) && { id: "overview", label: "Overview" },
    groups.length && { id: "specs", label: "Specifications" },
    p.package_contents?.length && { id: "box", label: "What's in the box" },
    (p.warranty || ic?.warranty?.text) && { id: "warranty", label: "Warranty & support" },
    docs.length && { id: "downloads", label: "Downloads" },
  ].filter(Boolean);
  const crumbs = p.breadcrumbs || [];

  return (
    <StoreLayout>
      <div className="mx-auto max-w-7xl px-4 pb-28 pt-6 sm:px-6" data-testid="product-detail">
        <Breadcrumbs testid="product-breadcrumbs" current={p.display_name}
          items={crumbs.length ? crumbs.map((c) => ({ to: `/c/${c.slug}`, label: c.name })) : p.brand?.has_page ? [{ to: `/brands/${p.brand.slug}`, label: p.brand.display_name }] : []} />
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-12">
          <div>
            <Gallery images={images} name={p.display_name} ph={tileProps(p)} />
            {p.bundle_image_note && <p className="mt-2 text-xs text-slate-500" data-testid="pdp-bundle-image-note">{p.bundle_image_note}</p>}
            {p.bundle_display_text && <p className="mt-1 text-sm text-slate-700" data-testid="pdp-bundle-display-text">{p.bundle_display_text.replace(/\s*\u2014\s*/g, " - ")}</p>}
          </div>
          <BuyBox ref={setAddEl} p={p} qty={qty} setQty={setQty} onAdd={add} adding={adding} highlights={highlights} />
        </div>
        <ClearancePdpBlock p={p} />
        <Suspense fallback={<div className="min-h-[60vh]" aria-hidden="true" />}>
          <Details p={p} ic={ic} html={html} groups={groups} docs={docs} sections={sections} />
        </Suspense>
      </div>
      <StickyBuyBar p={p} target={addEl} qty={qty} setQty={setQty} onAdd={add} adding={adding} />
    </StoreLayout>
  );
}

